import { BUSINESS_PLAN_TYPES, IBusinessToolType } from "constants/businessPlan";
import { t } from 'i18next'
import { IOrganizationalStructureData } from "PDF/components/OrganizationalStructure/types";
import { formatDateForPDF } from "PDF/helpers/formatDates";
import { isNotEmptyObj } from "PDF/helpers/object";
import { getMagazineTextObject } from "PDF/helpers/pdfMapping";

import { IBusinessToolForm } from "components/BusinessToolForm/utils/form";

import { IKeyPersonnelProfilesTableItem } from "./components/KeyPersonnelProfilesTable/types";
import { IProfilesTableItem } from "./components/ProfilesTable/types";

export const getMagazineItems = (businessPlanChapters: IBusinessToolForm, businessPlanType: IBusinessToolType, isExamplePage: boolean) => {
  const {
    managementTeam: { history, skills, personalQualities },
    externalPartnersAndAdvisers: { criteria, partners, plans },
    staffOrganization: {
      numberOfStaff,
      skills: staffOrganizationSkills,
      additionalHires,
      approaches,
      attractingDifficulties,
      salaryPolicy,
      retentionDifficulties,
      developmentPrograms,
    }
  } = businessPlanChapters.MANAGEMENT_AND_ORGANIZATION;

  if (businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    return [
      [
        getMagazineTextObject(isExamplePage ? 'History and Background. ' : t('pdfPages.mappings.managementAndOrganization.historyAndBackground'), history),
      ],
      [
        getMagazineTextObject(isExamplePage ? 'Staff Number and Structure. ' : t('pdfPages.mappings.managementAndOrganization.staffNumberAndStructure'), numberOfStaff),
      ],
      [
        getMagazineTextObject(isExamplePage ? 'Skills and Qualifications. ' : t('pdfPages.mappings.managementAndOrganization.skillsAndQualifications'), staffOrganizationSkills),
        getMagazineTextObject(isExamplePage ? 'Hiring Needs. ' : t('pdfPages.mappings.managementAndOrganization.hiringNeeds'), additionalHires),
        getMagazineTextObject(isExamplePage ? 'Salary and Motivation. ' : t('pdfPages.mappings.managementAndOrganization.salaryAndMotivation'), salaryPolicy),
      ],
    ];
  }

  return [
    [
      getMagazineTextObject(isExamplePage ? 'History and Background. ' : t('pdfPages.mappings.managementAndOrganization.historyAndBackground'), history),
      getMagazineTextObject(isExamplePage ? 'Skills and Competencies. ' : t('pdfPages.mappings.managementAndOrganization.skillsAndCompetencies'), skills),
      getMagazineTextObject(isExamplePage ? 'Personal Qualities. ' : t('pdfPages.mappings.managementAndOrganization.personalQualities'), personalQualities),
    ],
    [
      getMagazineTextObject(isExamplePage ? 'Partner Expertise. ' : t('pdfPages.mappings.managementAndOrganization.partnerExpertise'), partners),
      getMagazineTextObject(isExamplePage ? 'Selection Criteria. ' : t('pdfPages.mappings.managementAndOrganization.selectionCriteria'), criteria),
      getMagazineTextObject(isExamplePage ? 'Expansion Plans. ' : t('pdfPages.mappings.managementAndOrganization.expansionPlans'), plans),
    ],
    [
      getMagazineTextObject(isExamplePage ? 'Staff Number and Structure. ' : t('pdfPages.mappings.managementAndOrganization.staffNumberAndStructure'), numberOfStaff),
    ],
    [
      getMagazineTextObject(isExamplePage ? 'Skills and Qualifications. ' : t('pdfPages.mappings.managementAndOrganization.skillsAndQualifications'), staffOrganizationSkills),
      getMagazineTextObject(isExamplePage ? 'Hiring Needs. ' : t('pdfPages.mappings.managementAndOrganization.hiringNeeds'), additionalHires),
      getMagazineTextObject(isExamplePage ? 'Recruitment Approaches. ' : t('pdfPages.mappings.managementAndOrganization.recruitmentApproaches'), approaches),
      getMagazineTextObject(isExamplePage ? 'Attraction Difficulties. ' : t('pdfPages.mappings.managementAndOrganization.attractionDifficulties'), attractingDifficulties),
      getMagazineTextObject(isExamplePage ? 'Salary and Motivation. ' : t('pdfPages.mappings.managementAndOrganization.salaryAndMotivation'), salaryPolicy),
      getMagazineTextObject(isExamplePage ? 'Retention Challenges. ' : t('pdfPages.mappings.managementAndOrganization.retentionChallenges'), retentionDifficulties),
      getMagazineTextObject(isExamplePage ? 'Training Programs. ' : t('pdfPages.mappings.managementAndOrganization.trainingPrograms'), developmentPrograms),
    ],
  ];
};

export const getKeyPersonnelProfiles = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean): IKeyPersonnelProfilesTableItem[] => {
  const {
    managementTeam: { personnelProfiles },
  } = businessPlanChapters.MANAGEMENT_AND_ORGANIZATION;

  return personnelProfiles.map(({
    name,
    surname,
    dateOfBirth,
    file,
    position,
    listOfInstitutions,
    workExperience,
    keySkills,
    achievements,
  }) => {
    const rows = []

    if (position) {
      rows.push({
        heading:isExamplePage ? 'Current Position' : t('management.team.currentPosition'),
        info: position,
      });
    }

    listOfInstitutions.forEach(({ name, specialization }) => {
      if (name || specialization) {
        let info = name;

        if (info) {
          info += ', '
        }

        info += specialization;

        rows.push({ heading:isExamplePage ? 'Education' : t('management.team.education'), info });
      }
    });

    workExperience.forEach(({ from, to, functions, position, Employer }) => {
      const infoArr = [];

      if (from && to) {
        infoArr.push(`${formatDateForPDF(from, 'YYYY', false)}-${formatDateForPDF(to, 'YYYY', false)}`)
      }

      if (Employer) {
        infoArr.push(Employer)
      }

      if (position) {
        infoArr.push(position)
      }

      if (infoArr.length) {
        rows.push({
          heading:isExamplePage ? 'Work Experience' : t('pdfPages.mappings.managementAndOrganization.workExp'),
          info: infoArr.join(', ')
        });
      }

      if (functions) {
        rows.push({
          heading:isExamplePage ? 'Functions' : t('management.team.functions'),
          info: functions,
        })
      }
    });

    if (keySkills) {
      rows.push({
        heading:isExamplePage ? 'Key Skills' : t('pdfPages.mappings.managementAndOrganization.keySkills'),
        info: keySkills,
      });
    }

    if (achievements) {
      rows.push({
        heading:isExamplePage ? 'Special Achievements' : t('pdfPages.mappings.managementAndOrganization.specAchievements'),
        info: achievements,
      });
    }

    return ({
      name,
      surname,
      dateOfBirth,
      photo: file?.[0]?.url,
      rows,
    })
  }).filter(isNotEmptyObj);
}

export const getProfiles = (businessPlanChapters: IBusinessToolForm, isExamplePage: boolean): IProfilesTableItem[] => {
  const {
    externalPartnersAndAdvisers: { listOfExternalPartners }
  } = businessPlanChapters.MANAGEMENT_AND_ORGANIZATION;

  return listOfExternalPartners.map(({ name, surname, file, role, keySkills, workExperience }) => {
    const rows = [];

    if (role) {
      rows.push({
        heading: isExamplePage ? 'Role in the Company' : t('pdfPages.mappings.managementAndOrganization.roleInCompany'),
        info: role,
      })
    }

    workExperience.forEach(({ position, employer }) => {
      const infoArr = [];

      if (employer) {
        infoArr.push(employer)
      }

      if (position) {
        infoArr.push(position)
      }

      if (infoArr.length) {
        rows.push({
          heading: isExamplePage ? 'Work Experience' : t('pdfPages.mappings.managementAndOrganization.workExp'),
          info: infoArr.join(', ')
        });
      }
    });

    if (keySkills) {
      rows.push({
        heading: isExamplePage ? 'Key Skills' : t('pdfPages.mappings.managementAndOrganization.keySkills') ,
        info: keySkills,
      })
    }

    return {
      name,
      surname,
      photo: file?.[0]?.url,
      rows,
    }
  }).filter(isNotEmptyObj);
}

export const getOrganizationalStructure = (businessPlanChapters: IBusinessToolForm): IOrganizationalStructureData => {
  const {
    staffOrganization: { level1Department, level2Department, level3Department = [] }
  } = businessPlanChapters.MANAGEMENT_AND_ORGANIZATION;

  return { level1Department, level2Department, level3Department };
}
